import { LinearProgress } from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { KycIdImage } from "@/types/kycReview";
import {
  dropzoneAccept,
  isAcceptedAttachmentFile,
  isPreviewableImage,
  MAX_ATTACHMENT_BYTES,
} from "../logic/attachments";
import { UploadEntry } from "../state/types";
import { rv } from "../tokens";
import { CheckIcon, CloudUploadIcon, EyeIcon, FileTextIcon, XIcon } from "./icons";
import ImageLightbox from "./ImageLightbox";
import { useDownloadUrl } from "./useDownloadUrl";

const GoldProgress = withStyles({
  root: { height: 6, borderRadius: 4, background: rv.goldLight },
  bar: { background: rv.gold },
})(LinearProgress);

const useStyles = makeStyles((theme) => ({
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: ".04em",
    color: rv.muted,
    marginBottom: 7,
  },
  required: { color: rv.errorFg },
  dropzone: {
    border: `1.5px dashed ${rv.inputStroke}`,
    borderRadius: 12,
    background: "#fff",
    padding: theme.spacing(3, 2),
    textAlign: "center",
    cursor: "pointer",
    color: rv.muted,
    transition: "border-color .15s ease, background .15s ease",
    outline: "none",
    "&:hover": { borderColor: rv.green500, background: rv.mint },
  },
  dropzoneActive: { borderColor: rv.green, background: rv.mint },
  dzText: { fontSize: 13.5, fontWeight: 600, color: rv.body, marginTop: 8 },
  dzCaption: { fontSize: 11.5, color: rv.muted3, marginTop: 4 },
  uploaded: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: rv.mint,
    border: `1.5px solid ${rv.verifiedBorder}`,
    borderRadius: 12,
    padding: theme.spacing(1.5, 1.75),
  },
  uploadedCheck: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: rv.verifiedBg,
    color: rv.verifiedFg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  fileName: {
    flexGrow: 1,
    fontSize: 13,
    color: rv.body,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  remove: {
    border: "none",
    background: "none",
    cursor: "pointer",
    color: rv.muted,
    display: "flex",
    padding: 4,
  },
  onfile: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    border: `1.5px solid ${rv.hairline}`,
    borderRadius: 12,
    padding: theme.spacing(1.5),
    background:
      "repeating-linear-gradient(45deg, #fbfbf9, #fbfbf9 10px, #f3f4f1 10px, #f3f4f1 20px)",
  },
  thumb: {
    width: 46,
    height: 46,
    borderRadius: 8,
    background: "#fff",
    border: `1px solid ${rv.hairline}`,
    color: rv.green,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  thumbButton: {
    position: "relative",
    width: 46,
    height: 46,
    borderRadius: 8,
    overflow: "hidden",
    padding: 0,
    background: "#fff",
    border: `1px solid ${rv.hairline}`,
    color: rv.green,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    "&:hover $thumbOverlay": { opacity: 1 },
  },
  thumbImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  thumbOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(19,71,53,.45)",
    color: "#fff",
    opacity: 0,
    transition: "opacity .15s ease",
  },
  onfileText: {
    flexGrow: 1,
    minWidth: 0,
    fontSize: 13,
    color: rv.body,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  onfileTag: { fontSize: 11, fontWeight: 700, color: rv.verifiedFg, textTransform: "uppercase", letterSpacing: ".05em" },
  onfileName: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  onfileActions: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
  view: {
    border: "none",
    background: "none",
    color: rv.green,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    padding: "6px 4px",
    font: "inherit",
  },
  replace: {
    border: `1.5px solid ${rv.green}`,
    background: "none",
    color: rv.green,
    borderRadius: 8,
    padding: "7px 14px",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
  },
  uploadingBox: {
    border: `1.5px solid ${rv.goldLight}`,
    borderRadius: 12,
    padding: theme.spacing(1.75),
    background: "#FEFBF3",
  },
  uploadingLabel: {
    fontSize: 13,
    color: rv.body,
    marginBottom: 8,
    display: "flex",
    justifyContent: "space-between",
  },
  error: {
    color: rv.errorFg,
    fontSize: 11.5,
    fontWeight: 500,
    marginTop: 6,
  },
}));

interface Props {
  label: string;
  required?: boolean;
  entry?: UploadEntry;
  onFileImage?: Pick<KycIdImage, "url" | "name" | "fileName" | "mimeType"> | null;
  error?: string;
  onFile: (file: File) => void;
  onRemove: () => void;
}

export default function UploadTile({
  label,
  required,
  entry,
  onFileImage,
  error,
  onFile,
  onRemove,
}: Props) {
  const classes = useStyles();
  const [reject, setReject] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const { url: resolvedUrl } = useDownloadUrl(onFileImage?.url);

  const accept = (file: File) => {
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setReject("File is larger than 20 MB.");
      return;
    }
    if (!isAcceptedAttachmentFile(file)) {
      setReject("Upload a PNG, JPG/JPEG, WEBP, HEIC/HEIF, or PDF file.");
      return;
    }
    setReject("");
    onFile(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: dropzoneAccept,
    multiple: false,
    onDrop: (files: File[]) => {
      const file = files[0];
      if (file) accept(file);
    },
    onDropRejected: () => {
      setReject("Upload a PNG, JPG/JPEG, WEBP, HEIC/HEIF, or PDF file.");
    },
  });

  const status = entry?.status;
  const shownError = error || reject || entry?.error;
  const onFileLabel =
    onFileImage?.fileName || onFileImage?.name || "On file";
  const previewable = isPreviewableImage(onFileImage) && !imgError;

  const openPreview = () => {
    if (!resolvedUrl) return;
    if (previewable) setLightboxOpen(true);
    else window.open(resolvedUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div>
      <span className={classes.label}>
        {label}
        {required && <span className={classes.required}> *</span>}
      </span>

      {status === "uploading" ? (
        <div className={classes.uploadingBox}>
          <div className={classes.uploadingLabel}>
            <span>{entry?.name || "Uploading\u2026"}</span>
            <span>{"Uploading\u2026"}</span>
          </div>
          <GoldProgress />
        </div>
      ) : status === "uploaded" ? (
        <div className={classes.uploaded}>
          <span className={classes.uploadedCheck}>
            <CheckIcon size={16} />
          </span>
          <span className={classes.fileName}>{entry?.name || "File uploaded"}</span>
          <button type="button" className={classes.remove} onClick={onRemove} aria-label="Remove file">
            <XIcon size={16} />
          </button>
        </div>
      ) : onFileImage ? (
        <div className={classes.onfile}>
          {resolvedUrl ? (
            <button
              type="button"
              className={classes.thumbButton}
              onClick={openPreview}
              aria-label={`Preview ${label}`}
            >
              {previewable ? (
                <>
                  <img
                    className={classes.thumbImg}
                    src={resolvedUrl}
                    alt=""
                    onError={() => setImgError(true)}
                  />
                  <span className={classes.thumbOverlay}>
                    <EyeIcon size={16} color="#fff" />
                  </span>
                </>
              ) : (
                <FileTextIcon size={20} />
              )}
            </button>
          ) : (
            <span className={classes.thumb}>
              <FileTextIcon size={20} />
            </span>
          )}
          <span className={classes.onfileText}>
            <span className={classes.onfileTag}>On file</span>
            <span className={classes.onfileName}>{onFileLabel}</span>
          </span>
          <div className={classes.onfileActions}>
            {resolvedUrl && (
              <button type="button" className={classes.view} onClick={openPreview}>
                <EyeIcon size={14} /> View
              </button>
            )}
            <span {...getRootProps()}>
              <input {...getInputProps()} />
              <button type="button" className={classes.replace}>
                Replace
              </button>
            </span>
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`${classes.dropzone} ${isDragActive ? classes.dropzoneActive : ""}`}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon size={26} color={rv.green} />
          <div className={classes.dzText}>Drag and drop a file here or browse</div>
          <div className={classes.dzCaption}>
            Supported files: PNG, JPG/JPEG, WEBP, HEIC/HEIF, and PDF, up to 20
            MB. HEIC/HEIF files will be converted for compatibility.
          </div>
        </div>
      )}

      {shownError && (
        <div className={classes.error} data-rv-error="true">
          {shownError}
        </div>
      )}

      <ImageLightbox
        open={lightboxOpen}
        src={previewable ? resolvedUrl : null}
        title={onFileLabel}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
}
